<template>
  <div
    v-if="feesV2Enabled"
    class="tw-space-y-5"
  >
    <div class="tw-flex tw-items-center tw-gap-3">
      <o-button
        :disabled="isUpdating || hasFeeScheduleErrors"
        variant="primary"
        size="small"
        @click="updateExpenseRatioArea"
      >
        {{ isUpdating ? 'Updating…' : 'Update Chart' }}
      </o-button>
      <span
        v-if="updateError"
        class="tw-text-xs tw-text-red-500"
      >{{ updateError }}</span>
      <span
        v-else-if="updateSuccess"
        class="tw-text-xs tw-text-green-600"
      >Updated</span>
    </div>

    <!-- ── Proposed Portfolio Advisory Fee ── -->
    <div v-if="checkupStore.optimizedPortfolio">
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
        <p class="tw-font-semibold tw-text-sm">
          Proposed Portfolio Advisory Fee
        </p>
      </div>
      <p
        v-if="feesV2Enabled"
        class="tw-text-xs tw-text-gray-400 tw-mb-2"
      >
        Defaults from
        <button
          class="tw-text-purple-600 hover:tw-underline tw-bg-transparent
            tw-border-none tw-cursor-pointer tw-p-0 tw-text-xs"
          @click="checkupStore.setActiveArea('Settings')"
        >
          global settings
        </button>.
        Override here for this session.
      </p>
      <template v-if="feesV2Enabled">
        <FeeScheduleTierList
          :tiers="proposedTiers"
          :errors="proposedTierErrors"
        />
        <div class="tw-mt-2">
          <o-field
            label="Annual Flat Fee ($)"
            label-size="small"
            style="margin: 0;"
            label-class="!tw-font-normal"
          >
            <FlatDollarInput
              v-model="proposedFlatDollar"
              root-class="tw-w-28"
            />
          </o-field>
        </div>
      </template>
      <o-field
        v-else
        label="Advisory Fee (%)"
        label-size="small"
        style="margin: 0;"
        label-class="!tw-font-normal"
      >
        <o-input
          v-model.number="checkupStore.fees.optimized.advisoryFee"
          step="0.001"
          placeholder="0.05"
          type="number"
          size="small"
          root-class="tw-w-36"
        />
      </o-field>
    </div>

    <hr v-if="checkupStore.optimizedPortfolio">

    <!-- ── Starting Portfolio Advisory Fee ── -->
    <div>
      <p class="tw-font-semibold tw-text-sm tw-mb-1">
        Starting Portfolio Advisory Fee
      </p>
      <p class="tw-text-xs tw-text-gray-400 tw-mb-2">
        Fee the client currently pays on the starting portfolio.
      </p>
      <template v-if="feesV2Enabled">
        <FeeScheduleTierList
          :tiers="currentTiers"
          :errors="currentTierErrors"
        />
        <div class="tw-mt-2">
          <o-field
            label="Annual Flat Fee ($)"
            label-size="small"
            style="margin: 0;"
            label-class="!tw-font-normal"
          >
            <FlatDollarInput
              v-model="currentFlatDollar"
              root-class="tw-w-28"
            />
          </o-field>
        </div>
      </template>
      <o-field
        v-else
        label="Advisory Fee (%)"
        label-size="small"
        style="margin: 0;"
        label-class="!tw-font-normal"
      >
        <o-input
          v-model.number="checkupStore.fees.portfolio.advisoryFee"
          step="0.001"
          placeholder="0.05"
          type="number"
          size="small"
          root-class="tw-w-36"
        />
      </o-field>
    </div>

    <hr>

    <!-- ── Benchmark Portfolio Advisory Fee ── -->
    <div>
      <p class="tw-font-semibold tw-text-sm tw-mb-1">
        Benchmark Portfolio Advisory Fee
      </p>
      <p class="tw-text-xs tw-text-gray-400 tw-mb-2">
        Fee charged on the benchmark portfolio (typically 0 for an index).
      </p>
      <template v-if="feesV2Enabled">
        <FeeScheduleTierList
          :tiers="benchmarkTiers"
          :errors="benchmarkTierErrors"
        />
        <div class="tw-mt-2">
          <o-field
            label="Annual Flat Fee ($)"
            label-size="small"
            style="margin: 0;"
            label-class="!tw-font-normal"
          >
            <FlatDollarInput
              v-model="benchmarkFlatDollar"
              root-class="tw-w-28"
            />
          </o-field>
        </div>
      </template>
      <o-field
        v-else
        label="Advisory Fee (%)"
        label-size="small"
        style="margin: 0;"
        label-class="!tw-font-normal"
      >
        <o-input
          v-model.number="checkupStore.fees.benchmark.advisoryFee"
          step="0.001"
          placeholder="0.05"
          type="number"
          size="small"
          root-class="tw-w-36"
        />
      </o-field>
    </div>

    <hr>

    <!-- ── Management Fees ── -->
    <div>
      <p class="tw-font-semibold tw-text-sm tw-mb-2">
        Management Fees (%)
      </p>
      <div class="tw-flex tw-flex-col tw-gap-2">
        <div
          v-if="checkupStore.portfolio?.name"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <o-tooltip
            multilined
            variant="primary"
            position="top"
            class="tw-shrink-0"
            :label="checkupStore.portfolio.name"
          >
            <span
              class="tw-text-xs tw-text-gray-500 tw-w-28 tw-truncate"
            >{{ checkupStore.portfolio.name }}</span>
          </o-tooltip>
          <o-input
            v-model.number="checkupStore.fees.portfolio.managementFee"
            step="0.001"
            placeholder="0.00"
            type="number"
            size="small"
            root-class="tw-flex-1"
          />
        </div>
        <div
          v-if="checkupStore.optimizedPortfolio?.name"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <o-tooltip
            multilined
            variant="primary"
            position="top"
            class="tw-shrink-0"
            :label="checkupStore.optimizedPortfolio.name"
          >
            <span
              class="tw-text-xs tw-text-gray-500 tw-w-28 tw-truncate"
            >{{ checkupStore.optimizedPortfolio.name }}</span>
          </o-tooltip>
          <o-input
            v-model.number="checkupStore.fees.optimized.managementFee"
            step="0.001"
            placeholder="0.00"
            type="number"
            size="small"
            root-class="tw-flex-1"
          />
        </div>
        <div
          v-if="checkupStore.benchmark?.name"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <o-tooltip
            multilined
            variant="primary"
            position="top"
            class="tw-shrink-0"
            :label="checkupStore.benchmark.name"
          >
            <span
              class="tw-text-xs tw-text-gray-500 tw-w-28 tw-truncate"
            >{{ checkupStore.benchmark.name }}</span>
          </o-tooltip>
          <o-input
            v-model.number="checkupStore.fees.benchmark.managementFee"
            step="0.001"
            placeholder="0.00"
            type="number"
            size="small"
            root-class="tw-flex-1"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Flag off: legacy collapsed advisory/management fee inputs (pre-feature behavior). -->
  <div v-else>
    <p
      v-if="!showFeesInput"
      class="tw-cursor-pointer"
      @click="toggleFeesInput"
    >
      + Add Advisory/Management Fees
    </p>
    <div v-else>
      <p
        class="tw-cursor-pointer"
        @click="toggleFeesInput"
      >
        Hide Advisory/Management Fees
      </p>
      <hr class="tw-mt-4">
      <div v-if="checkupStore.portfolio?.name">
        <p class="tw-mt-4 tw-font-bold">
          {{ checkupStore.portfolio.name }} Fees
        </p>
        <div class="tw-mt-2 tw-flex tw-gap-5">
          <o-field
            label="Advisory Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.portfolio.advisoryFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
          <o-field
            label="Management Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.portfolio.managementFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
        </div>
      </div>
      <div v-if="checkupStore.benchmark?.name">
        <p class="tw-mt-4 tw-font-bold">
          {{ checkupStore.benchmark?.name || 'Benchmark' }} Fees
        </p>
        <div class="tw-my-2 tw-flex tw-gap-5">
          <o-field
            label="Advisory Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.benchmark.advisoryFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
          <o-field
            label="Management Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.benchmark.managementFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
        </div>
      </div>
      <div v-if="checkupStore.optimizedPortfolio?.name">
        <p class="tw-mt-4 tw-font-bold">
          {{ checkupStore.optimizedPortfolio?.name || 'Proposed Portfolio' }} Fees
        </p>
        <div class="tw-my-2 tw-flex tw-gap-5">
          <o-field
            label="Advisory Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.optimized.advisoryFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
          <o-field
            label="Management Fee (%)"
            label-size="small"
            style="margin: 0; flex-grow: 0;"
            label-class="!tw-font-normal"
          >
            <o-input
              v-model="checkupStore.fees.optimized.managementFee"
              step="0.001"
              placeholder="0.05"
              type="number"
              size="small"
              root-class="tw-w-36"
            />
          </o-field>
        </div>
      </div>
      <o-button
        variant="primary"
        @click="updateExpenseRatioArea"
      >
        Update Chart
      </o-button>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { getExpenseRatio, validateFeeSchedule, withTierId } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import FeeScheduleTierList from '@frontend/ui/features/checkup/components/FeeScheduleTierList.vue';
import FlatDollarInput from '@frontend/ui/features/checkup/components/FlatDollarInput.vue';

export default {
  name: 'AdvisorFeeInput',
  components: { FeeScheduleTierList, FlatDollarInput },
  props: [],
  data() {
    return {
      proposedTiers: [withTierId({ upTo: null, rate: 0 })],
      proposedFlatDollar: 0,
      currentTiers: [withTierId({ upTo: null, rate: 0 })],
      currentFlatDollar: 0,
      benchmarkTiers: [withTierId({ upTo: null, rate: 0 })],
      benchmarkFlatDollar: 0,
      isUpdating: false,
      updateSuccess: false,
      updateError: null,
      // Legacy collapse state for the flag-off advisory/management fee inputs.
      showFeesInput: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore),
    // Tiered/flat-dollar advisory fee schedules are gated behind this flag. When off,
    // the UI falls back to a single advisory-fee percentage per portfolio (legacy behavior).
    feesV2Enabled() {
      return this.checkupStore.featureFlags.includes('Feature: Advisor Fees');
    },
    proposedSchedule() {
      return {
        tiers: this.proposedTiers.map((t, i, arr) => ({
          // Last tier is implicitly unlimited.
          upTo: i === arr.length - 1 ? null : Number(t.upTo),
          rate: Number(t.rate),
        })),
        flat_dollar: this.proposedFlatDollar || 0,
      };
    },
    currentSchedule() {
      return {
        tiers: this.currentTiers.map((t, i, arr) => ({
          upTo: i === arr.length - 1 ? null : Number(t.upTo),
          rate: Number(t.rate),
        })),
        flat_dollar: this.currentFlatDollar || 0,
      };
    },
    benchmarkSchedule() {
      return {
        tiers: this.benchmarkTiers.map((t, i, arr) => ({
          upTo: i === arr.length - 1 ? null : Number(t.upTo),
          rate: Number(t.rate),
        })),
        flat_dollar: this.benchmarkFlatDollar || 0,
      };
    },
    feeInputsSnapshot() {
      return JSON.stringify({
        p: this.proposedSchedule,
        c: this.currentSchedule,
        b: this.benchmarkSchedule,
        pm: this.checkupStore.fees.portfolio?.managementFee ?? 0,
        bm: this.checkupStore.fees.benchmark?.managementFee ?? 0,
        om: this.checkupStore.fees.optimized?.managementFee ?? 0,
      });
    },
    proposedTierErrors() {
      return validateFeeSchedule(this.proposedTiers);
    },
    currentTierErrors() {
      return validateFeeSchedule(this.currentTiers);
    },
    benchmarkTierErrors() {
      return validateFeeSchedule(this.benchmarkTiers);
    },
    hasFeeScheduleErrors() {
      if (!this.feesV2Enabled) return false;
      return this.proposedTierErrors.length > 0 ||
        this.currentTierErrors.length > 0 ||
        this.benchmarkTierErrors.length > 0;
    },
  },
  watch: {
    feeInputsSnapshot() {
      if (this.updateSuccess) this.updateSuccess = false;
      if (this.updateError) this.updateError = null;
    },
  },
  created() {
    // Seeding tiered/flat-dollar schedules and per-portfolio fee defaults is part of the
    // gated feature. When the flag is off, leave fees at their existing (legacy) values.
    if (!this.feesV2Enabled) return;
    const defaults = this.advisorStore.portfolioFeeDefaults || {};
    const advisoryFromSlot = slot => {
      const advisory = slot?.advisory || {};
      const tiers = advisory.tiers?.length ?
        advisory.tiers.map(t => withTierId(t)) :
        [withTierId({ upTo: null, rate: 0 })];
      return { tiers, flat_dollar: advisory.flat_dollar || 0 };
    };

    // Proposed defaults to the proposed slot of the global fee defaults
    const proposed = this.checkupStore.fees.schedule || advisoryFromSlot(defaults.proposed);
    this.proposedTiers = proposed.tiers?.length ?
      proposed.tiers.map(t => (t.tierId == null ? withTierId(t) : t)) :
      [withTierId({ upTo: null, rate: 0 })];
    this.proposedFlatDollar = proposed.flat_dollar || 0;

    // Starting portfolio defaults to the starting slot
    const current = this.checkupStore.fees.currentPortfolioSchedule || advisoryFromSlot(defaults.starting);
    this.currentTiers = current.tiers?.length ?
      current.tiers.map(t => (t.tierId == null ? withTierId(t) : t)) :
      [withTierId({ upTo: null, rate: 0 })];
    this.currentFlatDollar = current.flat_dollar || 0;

    // Benchmark defaults to the benchmark slot
    const benchmark = this.checkupStore.fees.benchmarkSchedule || advisoryFromSlot(defaults.benchmark);
    this.benchmarkTiers = benchmark.tiers?.length ?
      benchmark.tiers.map(t => (t.tierId == null ? withTierId(t) : t)) :
      [withTierId({ upTo: null, rate: 0 })];
    this.benchmarkFlatDollar = benchmark.flat_dollar || 0;

    // Seed per-portfolio management fees from the global defaults if not already set.
    if (!this.checkupStore.fees.portfolio.managementFee) {
      this.checkupStore.fees.portfolio.managementFee = Number(defaults.starting?.management) || 0;
    }
    if (!this.checkupStore.fees.optimized.managementFee) {
      this.checkupStore.fees.optimized.managementFee = Number(defaults.proposed?.management) || 0;
    }
    if (!this.checkupStore.fees.benchmark.managementFee) {
      this.checkupStore.fees.benchmark.managementFee = Number(defaults.benchmark?.management) || 0;
    }
  },
  methods: {
    buildExtraData(rest) {
      const p = this.checkupStore.portfolio;
      const b = this.checkupStore.benchmark;
      const o = this.checkupStore.optimizedPortfolio;
      const fmtPct = v => (v * 100).toFixed(2) + '%';
      // Total Fees includes expense ratio + advisory + management + flat-as-pct of the shared AUM.
      const fmtTotal = (er, extra, flatPct) => (((er || 0) + (extra || 0) + (flatPct || 0)) * 100).toFixed(2) + '%';
      const fmtFlatWithPct = (dollars, pct) => {
        const money = dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
        return (pct || 0) > 0 ? `${money} (${(pct * 100).toFixed(2)}%)` : money;
      };
      const data = [];
      if (p) {
        const totalPct = fmtTotal(
            p.average_expense_ratio, rest.portfolioAdditionalFees, rest.portfolioFlatDollarPct,
        );
        data.push(
            { name: `${p.name} Avg Expense Ratio`, value: fmtPct(p.average_expense_ratio) },
            { name: `${p.name} Advisory Fee`, value: fmtPct(rest.portfolioAdvisoryFee) },
            { name: `${p.name} Management Fee`, value: fmtPct(rest.portfolioMgmtFee) },
            ...(this.feesV2Enabled ? [{ name: `${p.name} Total Fees`, value: totalPct }] : []),
        );
        if ((rest.portfolioFlatDollar || 0) > 0) {
          const flatLabel = fmtFlatWithPct(rest.portfolioFlatDollar, rest.portfolioFlatDollarPct);
          data.push({ name: `${p.name} Annual Flat Fee`, value: flatLabel });
        }
      }
      if (b) {
        const totalPct = fmtTotal(
            b.average_expense_ratio, rest.benchmarkAdditionalFees, rest.benchmarkFlatDollarPct,
        );
        data.push(
            { name: `${b.name} Avg Expense Ratio`, value: fmtPct(b.average_expense_ratio) },
            { name: `${b.name} Advisory Fee`, value: fmtPct(rest.benchmarkAdvisoryFee) },
            { name: `${b.name} Management Fee`, value: fmtPct(rest.benchmarkMgmtFee) },
            ...(this.feesV2Enabled ? [{ name: `${b.name} Total Fees`, value: totalPct }] : []),
        );
        if ((rest.benchmarkFlatDollar || 0) > 0) {
          const flatLabel = fmtFlatWithPct(rest.benchmarkFlatDollar, rest.benchmarkFlatDollarPct);
          data.push({ name: `${b.name} Annual Flat Fee`, value: flatLabel });
        }
      }
      if (o) {
        const totalPct = fmtTotal(
            o.average_expense_ratio, rest.proposedAdditionalFees, rest.proposedFlatDollarPct,
        );
        data.push(
            { name: `${o.name} Avg Expense Ratio`, value: fmtPct(o.average_expense_ratio) },
            { name: `${o.name} Advisory Fee`, value: fmtPct(rest.proposedAdvisoryFee) },
            { name: `${o.name} Management Fee`, value: fmtPct(rest.proposedMgmtFee) },
            ...(this.feesV2Enabled ? [{ name: `${o.name} Total Fees`, value: totalPct }] : []),
        );
        if ((rest.proposedFlatDollar || 0) > 0) {
          const flatLabel = fmtFlatWithPct(rest.proposedFlatDollar, rest.proposedFlatDollarPct);
          data.push({ name: `${o.name} Annual Flat Fee`, value: flatLabel });
        }
      }
      return data;
    },
    async updateExpenseRatioArea() {
      if (!this.feesV2Enabled) {
        try {
          console.log('ADVISOR STORE', this.advisorStore);
          const { chartData, ...rest } = getExpenseRatio(this.checkupStore.portfolio, this.checkupStore.benchmark, this.checkupStore.optimizedPortfolio, this.checkupStore.fees.portfolio, this.checkupStore.fees.benchmark, this.checkupStore.fees.optimized, this.advisorStore);
          this.checkupStore.updateArea('Fees', {
            name: 'Fees',
            ...rest,
            chartData: [{
              ...chartData,
              type: 'line',
              options: {
                scales: {
                  x: {
                    title: { display: true, text: 'Year' },
                  },
                  y: {
                    title: { display: true, text: 'Cumulative Fees' },
                  },
                },
              },
              formats: { y: 'currency' },
            }],
            extraData: [
              ...(this.checkupStore.portfolio ? [
                { name: `${this.checkupStore.portfolio.name || 'Portfolio'} Avg Expense Ratio`, value: (this.checkupStore.portfolio.average_expense_ratio * 100).toFixed(2) + '%' },
                { name: `${this.checkupStore.portfolio.name || 'Portfolio'} Advisory and Mgmt Fees`, value: (rest.portfolioAdditionalFees * 100).toFixed(2) + '%' },
              ] : []),
              ...(this.checkupStore.benchmark ? [
                { name: `${this.checkupStore.benchmark.name || 'Benchmark'} Avg Expense Ratio`, value: (this.checkupStore.benchmark.average_expense_ratio * 100).toFixed(2) + '%' },
                { name: `${this.checkupStore.benchmark.name || 'Benchmark'} Advisory and Mgmt Fees`, value: (rest.benchmarkAdditionalFees * 100).toFixed(2) + '%' },
              ] : []),
              ...(this.checkupStore.optimizedPortfolio ? [
                { name: `${this.checkupStore.optimizedPortfolio.name || 'Optimized'} Avg Expense Ratio`, value: (this.checkupStore.optimizedPortfolio.average_expense_ratio * 100).toFixed(2) + '%' },
                { name: `${this.checkupStore.optimizedPortfolio.name || 'Optimized'} Advisory and Mgmt Fees`, value: (rest.proposedAdditionalFees * 100).toFixed(2) + '%' },
              ] : []),
            ],
            topLeftComponent: {
              component: 'AdvisorFeeInput',
              props: {},
            },
          });
          console.log('CREATE ExpenseRatio DATA', chartData);
          await this.checkupStore.updateAreaExplanationWithFetch('Fees');
        } catch (error) {
          console.error('error', error);
        }
        return;
      }
      if (this.hasFeeScheduleErrors) {
        this.updateError = 'Fix fee schedule errors before updating.';
        return;
      }
      this.isUpdating = true;
      this.updateSuccess = false;
      this.updateError = null;
      try {
        // When the feature is off, pass null schedules so getExpenseRatio falls back to the
        // simple advisory-fee percentages, and leave the schedule fields unset.
        const proposedSchedule = this.feesV2Enabled ? this.proposedSchedule : null;
        const currentSchedule = this.feesV2Enabled ? this.currentSchedule : null;
        const benchmarkSchedule = this.feesV2Enabled ? this.benchmarkSchedule : null;
        this.checkupStore.fees.schedule = proposedSchedule;
        this.checkupStore.fees.currentPortfolioSchedule = currentSchedule;
        this.checkupStore.fees.benchmarkSchedule = benchmarkSchedule;

        const { chartData, ...rest } = getExpenseRatio(
            this.checkupStore.portfolio,
            this.checkupStore.benchmark,
            this.checkupStore.optimizedPortfolio,
            this.checkupStore.fees.portfolio,
            this.checkupStore.fees.benchmark,
            this.checkupStore.fees.optimized,
            this.advisorStore,
            proposedSchedule,
            currentSchedule,
            benchmarkSchedule,
            this.feesV2Enabled,
        );

        this.checkupStore.updateArea('Fees', {
          name: 'Fees',
          ...rest,
          feeSchedule: proposedSchedule,
          currentFeeSchedule: currentSchedule,
          benchmarkFeeSchedule: benchmarkSchedule,
          // Legacy single-flat-fee field for ExportArea's legend; mirrors proposed flat dollar.
          flatDollarFee: rest.proposedFlatDollar || 0,
          extraData: this.buildExtraData(rest),
          chartData: [{
            ...chartData,
            type: 'line',
            options: {
              scales: {
                x: { title: { display: true, text: 'Year' } },
                y: { title: { display: true, text: 'Cumulative Fees' } },
              },
            },
            formats: { y: 'currency' },
          }],
        });

        this.updateSuccess = true;
      } catch (error) {
        console.error('Fee update error:', error);
        this.updateError = 'Update failed — check console';
      } finally {
        this.isUpdating = false;
      }

      // Regenerate the AI explanation in the background; the chart/store
      // update is already done, so the user shouldn't wait on the AI worker.
      this.checkupStore.updateAreaExplanationWithFetch('Fees').catch(err => {
        console.error('Fee explanation refresh failed:', err);
      });
    },
    toggleFeesInput() {
      this.showFeesInput = !this.showFeesInput;
    },
  },
};
</script>
